import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import Layout from '../../layout/Layout';
import { getHeaders } from '../../utility/getHeader';
import fetchData from '../../functions/fetchData';

export default function QuotationHistory() {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [quotationToDelete, setQuotationToDelete] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50); // Number of items per page (same as billhistory)
  
  // Calculate pagination values
  const totalPages = Math.ceil(filteredQuotations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentQuotations = filteredQuotations.slice(startIndex, endIndex);

  // Check if user is cashier
  const userType = localStorage.getItem('usertype') || sessionStorage.getItem('usertype');
  const isCashier = userType === 'Cashier';
  
  // Navigate to dashboard function
  const navigateToDashboard = () => {
    if (isCashier) {
      navigate('/CashierDashboard');
    } else {
      navigate('/dashboard');
    }
  };

  // Light color theme styles
  const lightTheme = {
    cardBackground: '#f8f9fa',
    headerBackground: '#e9ecef',
    tableHeader: '#dee2e6',
    primaryButton: '#0d6efd',
    successButton: '#198754',
    warningButton: '#ffc107',
    dangerButton: '#dc3545',
    infoButton: '#0dcaf0',
    textMuted: '#6c757d',
    borderColor: '#dee2e6'
  };

  // Helper function to convert BLOB to base64
  const blobToBase64 = (blob, mimeType) => {
    if (!blob) return null;
    try {
      let uint8Array;
      if (blob.data) {
        uint8Array = new Uint8Array(blob.data);
      } else if (Array.isArray(blob)) {
        uint8Array = new Uint8Array(blob);
      } else {
        uint8Array = new Uint8Array(blob);
      }
      const base64 = btoa(String.fromCharCode.apply(null, uint8Array));
      return `data:${mimeType};base64,${base64}`;
    } catch (error) {
      console.error('Error converting BLOB to base64:', error);
      return null;
    }
  };

  // Fetch quotations on component mount
  useEffect(() => {
    fetchQuotations();
    fetchCompanyProfile();
  }, []);

  // Filter quotations based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredQuotations(quotations);
    } else {
      const filtered = quotations.filter(quotation =>
        quotation.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        quotation.quotation_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        quotation.customer_phone?.includes(searchTerm)
      );
      setFilteredQuotations(filtered);
    }
    // Reset to first page when search changes
    setCurrentPage(1);
  }, [searchTerm, quotations]);

  // Pagination functions
  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const goToPage = (page) => setCurrentPage(page);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const fetchCompanyProfile = async () => {
    try {
      const response = await fetchData('company_profile', null, 'id', {});
      console.log('Company profile response:', response);
      if (response && response.length > 0) {
        setCompanyProfile(response[0]);
        console.log('Company profile loaded:', response[0]);
      } else {
        console.warn('No company profile data found');
      }
    } catch (error) {
      console.error('Error fetching company profile:', error);
      toast.error('Failed to load company profile');
    }
  };

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const response = await fetchData('quotations', null, 'id', {});
      if (Array.isArray(response)) {
        // Sort by creation date (newest first)
        const sortedQuotations = response.sort((a, b) => new Date(b.setup_date) - new Date(a.setup_date));
        setQuotations(sortedQuotations);
      }
    } catch (error) {
      console.error('Error fetching quotations:', error);
      toast.error('Error loading quotations');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (quotation) => {
    // Navigate to quotation page with edit data
    navigate('/quotation', { 
      state: { 
        editQuotation: quotation,
        mode: 'edit'
      } 
    });
  };

  const handleDelete = async (quotationId) => {
    try {
      await axios.delete(`/deletedata/quotations/${quotationId}`, getHeaders());
      
      // Also delete related quotation items
      await axios.delete(`/deletedata/quotation_items/quotation/${quotationId}`, getHeaders());
      
      toast.success('Quotation deleted successfully');
      fetchQuotations(); // Refresh the list
      setShowDeleteModal(false);
      setQuotationToDelete(null);
    } catch (error) {
      console.error('Error deleting quotation:', error);
      toast.error('Error deleting quotation');
    }
  };

  const handlePrint = async (quotation) => {
    try {
      // Fetch quotation items for this quotation using fetchData
      const itemsResponse = await fetchData('quotation_items', null, 'id', { order_id: quotation.id });
      const items = Array.isArray(itemsResponse) ? itemsResponse : [];

      // Prepare quotation data for printing
      const quotationData = {
        ...quotation,
        quotation_number: quotation.quotation_number || `Q${quotation.id}`,
        items: items.map(item => ({
          item_name: item.item_name,
          item_details: item.item_details,
          quantity: item.quantity,
          rate: item.rate,
          total_price: item.total_price,
          total_amount: item.total_amount
        }))
      };

      printQuotation(quotationData);
    } catch (error) {
      console.error('Error preparing quotation for print:', error);
      toast.error('Error preparing quotation for printing');
    }
  };

  // Print Quotation Function
  const printQuotation = (lastSavedQuotation) => {
    if (!lastSavedQuotation) {
      toast.error('No quotation data available for printing. Please save the quotation first.');
      return;
    }

    if (!companyProfile) {
      toast.error('Company profile not loaded. Please try again.');
      return;
    }

    // Use quotation_number for display, fallback to id if quotation_number is not available
    const quotationNumber = lastSavedQuotation.quotation_number || lastSavedQuotation.id || 'DRAFT';

    // Convert logo to base64 if available
    const logoSrc = companyProfile?.logo && companyProfile?.logo_type 
      ? blobToBase64(companyProfile.logo, companyProfile.logo_type) 
      : '/logo.png';

    const printWindow = window.open('', '_blank');
   printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Quotation - ${quotationNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: Arial, sans-serif;
            padding: 30px;
            background: white;
            font-size: 11px;
          }
          .container {
            max-width: 800px;
            margin: 0 auto;
            border: 1px solid #ddd;
          }
          .header-section {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding: 15px 20px;
            border-bottom: 1px solid #ddd;
          }
          .company-info {
            display: flex;
            gap: 15px;
            align-items: center;
          }
          .logo {
            width: 80px;
            height: 80px;
            object-fit: contain;
          }
          .company-details h2 {
            color: #FF6B35;
            font-size: 16px;
            margin-bottom: 3px;
          }
          .company-details p {
            font-size: 9px;
            color: #666;
            line-height: 1.3;
            margin: 2px 0;
          }
          .header-contact {
            text-align: right;
            font-size: 9px;
            color: #666;
            line-height: 1.5;
          }
          .title-bar {
            background: #FF6B35;
            color: black;
            text-align: center;
            padding: 8px;
            font-size: 16px;
            font-weight: bold;
            letter-spacing: 2px;
          }
          .info-section {
            display: flex;
            border-bottom: 1px solid #ddd;
          }
          .invoice-info, .customer-info {
            flex: 1;
            padding: 12px 20px;
          }
          .invoice-info {
            border-right: 1px solid #ddd;
          }
          .info-section h4 {
            font-size: 10px;
            color: #333;
            margin-bottom: 8px;
            padding-bottom: 3px;
            border-bottom: 1px solid #eee;
          }
          .info-row {
            display: flex;
            font-size: 9px;
            margin: 4px 0;
          }
          .info-label {
            font-weight: 600;
            min-width: 90px;
            color: #333;
          }
          .info-value {
            color: #555;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
          }
          .items-table th {
            background: #666;
            color: white;
            padding: 8px 10px;
            text-align: left;
            font-size: 10px;
            font-weight: 600;
          }
          .items-table td {
            padding: 10px 10px;
            border-bottom: 1px solid #eee;
            font-size: 10px;
            vertical-align: top;
          }
          .items-table tr:last-child td {
            border-bottom: none;
          }
          .item-description {
            font-weight: 600;
            color: #0066cc;
            margin-bottom: 4px;
          }
          .item-details {
            font-size: 9px;
            color: #666;
            font-style: italic;
            line-height: 1.4;
            margin-top: 4px;
          }
          .text-center { text-align: center; }
          .text-right { text-align: right; }
          .watermark {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-45deg);
            font-size: 120px;
            color: rgba(200, 200, 200, 0.15);
            font-weight: bold;
            z-index: -1;
            pointer-events: none;
          }
          .summary-section {
            border-top: 2px solid #333;
            padding: 0;
          }
          .summary-table {
            width: 100%;
            border-collapse: collapse;
          }
          .summary-row {
            display: flex;
            justify-content: flex-end;
            border-bottom: 1px solid #eee;
          }
          .summary-content {
            width: 300px;
            display: flex;
            justify-content: space-between;
            padding: 6px 20px;
            font-size: 10px;
          }
          .summary-label {
            color: #555;
          }
          .summary-value {
            font-weight: 600;
            color: #333;
          }
          .grand-total-row {
            background: #FF6B35;
            color: white;
            border: none;
          }
          .grand-total-row .summary-content {
            font-size: 12px;
            font-weight: bold;
          }
          .footer-section {
            display: flex;
            padding: 15px 20px;
            gap: 20px;
          }
          .terms-section {
            flex: 1;
          }
          .terms-section h4 {
            color: #FF6B35;
            font-size: 10px;
            margin-bottom: 5px;
          }
          .terms-section p {
            font-size: 8px;
            color: #666;
            line-height: 1.4;
            margin: 3px 0;
          }
          .bank-section {
            width: 280px;
            border: 1px solid #ddd;
            padding: 10px;
            background: #f9f9f9;
          }
          .bank-section h4 {
            color: #333;
            font-size: 10px;
            margin-bottom: 8px;
            padding-bottom: 3px;
            border-bottom: 1px solid #ddd;
          }
          .bank-row {
            display: flex;
            font-size: 9px;
            margin: 3px 0;
          }
          .bank-label {
            font-weight: 600;
            min-width: 90px;
            color: #333;
          }
          .bank-value {
            color: #555;
          }
          .signature-section {
            border-top: 1px solid #ddd;
            padding: 15px 20px;
            text-align: right;
          }
          .signature-box {
            display: inline-block;
            text-align: center;
            min-width: 200px;
          }
          .signature-line {
            border-top: 1px solid #333;
            margin-top: 50px;
            padding-top: 5px;
            font-size: 10px;
            font-weight: 600;
            color: #333;
          }
          .footer-note {
            text-align: center;
            padding: 8px;
            background: #f5f5f5;
            font-size: 8px;
            color: #666;
            font-style: italic;
          }
          @media print {
            body { padding: 0; }
            .container { border: none; }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <!-- Header -->
          <div class="header-section">
            <div class="company-info">
              <img src="${logoSrc}" alt="Logo" class="logo" onerror="this.style.display='none'" />
              <div class="company-details">
                <h2>${companyProfile.name || 'Company Name'}</h2>
                <p><strong>Tax ID:</strong> ${companyProfile.tax_id || companyProfile.taxid || '0000000000000'}</p>
                <p>${companyProfile.address || ''}</p>
                <p>${companyProfile.city ? companyProfile.city + ', ' : ''}${companyProfile.state || ''} ${companyProfile.zipcode || ''}</p>
              </div>
            </div>
            <div class="header-contact">
              <p>📞 ${companyProfile.phone_number || ''}</p>
              <p>🌐 ${companyProfile.website || 'https://www.cloudnetsoftwares.com'}</p>
              <p>📧 ${companyProfile.email || 'cloudnetsoftwares@gmail.com'}</p>
            </div>
          </div>

          <!-- Title Bar -->
          <div class="title-bar">QUOTATION</div>

          <!-- Invoice & Customer Info -->
          <div class="info-section">
            <div class="invoice-info">
              <h4>📋 Quotation Information</h4>
              <div class="info-row">
                <span class="info-label">Quotation Number:</span>
                <span class="info-value">${quotationNumber}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Quotation Date:</span>
                <span class="info-value">${new Date(lastSavedQuotation.setup_date).toLocaleDateString('en-GB')}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Valid Until:</span>
                <span class="info-value">${new Date(lastSavedQuotation.valid_until).toLocaleDateString('en-GB')}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Payment Method:</span>
                <span class="info-value">${lastSavedQuotation.payment_mode || 'Cash'}</span>
              </div>
            </div>
            <div class="customer-info">
              <h4>👤 Customer Details</h4>
              <div class="info-row">
                <span class="info-label">Name:</span>
                <span class="info-value">${lastSavedQuotation.customer_name || 'Walk-in Customer'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Phone:</span>
                <span class="info-value">${lastSavedQuotation.customer_phone || 'N/A'}</span>
              </div>
              ${lastSavedQuotation.customer_email ? `<div class="info-row">
                <span class="info-label">Email:</span>
                <span class="info-value">${lastSavedQuotation.customer_email}</span>
              </div>` : ''}
              ${lastSavedQuotation.customer_address ? `<div class="info-row">
                <span class="info-label">Address:</span>
                <span class="info-value">${lastSavedQuotation.customer_address}</span>
              </div>` : ''}
            </div>
          </div>

          <!-- Items Table -->
          <table class="items-table">
            <thead>
              <tr>
                <th width="5%" class="text-center">#</th>
                <th width="40%">Item Description</th>
                <th width="12%" class="text-right">Unit Price</th>
                <th width="8%" class="text-center">Qty</th>
                <th width="12%" class="text-right">Amount</th>
                <th width="10%" class="text-right">Disc%</th>
                <th width="13%" class="text-right">Net Amount</th>
              </tr>
            </thead>
            <tbody>
              ${lastSavedQuotation.items && lastSavedQuotation.items.length > 0 ? lastSavedQuotation.items.map((item, index) => `
                <tr>
                  <td class="text-center">${index + 1}</td>
                  <td>
                    <div class="item-description">${item.item_name || 'N/A'}</div>
                    ${item.item_details ? `<div class="item-details">${item.item_details}</div>` : ''}
                  </td>
                  <td class="text-right">฿ ${parseFloat(item.rate || 0).toFixed(2)}</td>
                  <td class="text-center">${parseFloat(item.quantity || 0)}</td>
                  <td class="text-right">฿ ${(parseFloat(item.rate || 0) * parseFloat(item.quantity || 1)).toFixed(2)}</td>
                  <td class="text-right">0%</td>
                  <td class="text-right">฿ ${parseFloat(item.total_price || 0).toFixed(2)}</td>
                </tr>
              `).join('') : '<tr><td colspan="7" class="text-center" style="padding: 20px;">No items found</td></tr>'}
            </tbody>
          </table>

          <!-- Page Break Space for Items -->
          <div style="min-height: 250px;"></div>

          <!-- Summary Section -->
          <div class="summary-section">
            <div class="summary-row">
              <div class="summary-content">
                <span class="summary-label">Subtotal:</span>
                <span class="summary-value">฿ ${parseFloat(lastSavedQuotation.subtotal || 0).toFixed(2)}</span>
              </div>
            </div>
            <div class="summary-row">
              <div class="summary-content">
                <span class="summary-label">Discount:</span>
                <span class="summary-value">- ฿ ${(parseFloat(lastSavedQuotation.subtotal || 0) - parseFloat(lastSavedQuotation.subtotal_afterdiscount || 0)).toFixed(2)}</span>
              </div>
            </div>
            <div class="summary-row grand-total-row">
              <div class="summary-content">
                <span class="summary-label">GRAND TOTAL:</span>
                <span class="summary-value">฿ ${parseFloat(lastSavedQuotation.grand_total || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          <!-- Footer Section with QR Code in Middle -->
          <div style="display: flex; padding: 15px 20px; gap: 15px; align-items: flex-start;">
            <div style="flex: 1;">
              <h4 style="color: #FF6B35; font-size: 10px; margin-bottom: 5px;">Terms & Conditions:</h4>
              ${companyProfile.terms_and_conditions ? companyProfile.terms_and_conditions.split('\n').map(line => `<p style="font-size: 8px; color: #666; line-height: 1.4; margin: 3px 0;">${line}</p>`).join('') : `
                <p style="font-size: 8px; color: #666; line-height: 1.4; margin: 3px 0;">• Payment is due upon receipt of invoice</p>
                <p style="font-size: 8px; color: #666; line-height: 1.4; margin: 3px 0;">• Late payments may incur additional charges</p>
                <p style="font-size: 8px; color: #666; line-height: 1.4; margin: 3px 0;">• Company liability limited to product value</p>
                <p style="font-size: 8px; color: #666; line-height: 1.4; margin: 3px 0;">• Customer responsible for order accuracy</p>
              `}
              <p style="margin-top: 8px; font-size: 8px; color: #666; font-style: italic;">For support, contact: ${companyProfile.email || 'support@cloudnetsoftwares.com'}</p>
            </div>
            
            ${companyProfile.qr_code && companyProfile.qr_code_type ? `
            <div style="text-align: center; padding: 0 15px;">
              <img src="${blobToBase64(companyProfile.qr_code, companyProfile.qr_code_type)}" 
                   alt="QR Code" 
                   style="max-width: 120px; max-height: 120px; object-fit: contain;" 
                   onerror="this.style.display='none'" />
            </div>
            ` : ''}
            
            <div style="width: 280px; border: 1px solid #ddd; padding: 10px; background: #f9f9f9;">
              <h4 style="color: #333; font-size: 10px; margin-bottom: 8px; padding-bottom: 3px; border-bottom: 1px solid #ddd;">💳 Bank Details</h4>
              <div style="display: flex; font-size: 9px; margin: 3px 0;">
                <span style="font-weight: 600; min-width: 90px; color: #333;">Bank Name:</span>
                <span style="color: #555;">${companyProfile.bank_name || 'Siam Commercial Bank'}</span>
              </div>
              <div style="display: flex; font-size: 9px; margin: 3px 0;">
                <span style="font-weight: 600; min-width: 90px; color: #333;">Account Name:</span>
                <span style="color: #555;">${companyProfile.account_name || companyProfile.name || 'Cloudnet Softwares Co., Ltd.'}</span>
              </div>
              <div style="display: flex; font-size: 9px; margin: 3px 0;">
                <span style="font-weight: 600; min-width: 90px; color: #333;">Account Number:</span>
                <span style="color: #555;">${companyProfile.account_number || '000-000-0000'}</span>
              </div>
              <div style="display: flex; font-size: 9px; margin: 3px 0;">
                <span style="font-weight: 600; min-width: 90px; color: #333;">Routing Number:</span>
                <span style="color: #555;">${companyProfile.routing_number || '0'}</span>
              </div>
              <div style="display: flex; font-size: 9px; margin: 3px 0;">
                <span style="font-weight: 600; min-width: 90px; color: #333;">SWIFT Code:</span>
                <span style="color: #555;">${companyProfile.swift_code || '0'}</span>
              </div>
            </div>
          </div>

          <!-- Signature -->
          <div class="signature-section">
            <div class="signature-box">
              <div class="signature-line">Authorized Signatory</div>
            </div>
          </div>

          <!-- Footer Note -->
          <div class="footer-note">
            This is a computer-generated quotation and does not require a physical signature.
          </div>
        </div>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    // Auto print after a short delay
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const confirmDelete = (quotation) => {
    setQuotationToDelete(quotation);
    setShowDeleteModal(true);
  };

  const renderContent = () => (
    <div style={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      <ToastContainer />
      
      {/* Dashboard Navigation Button - Only for cashiers */}
      {isCashier && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 1000 }}>
          <button
            className="btn"
            onClick={navigateToDashboard}
            style={{
              backgroundColor: lightTheme.primaryButton,
              color: 'white',
              padding: '10px 15px',
              fontSize: '14px',
              borderRadius: '25px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              border: 'none'
            }}
          >
            🏠 Dashboard
          </button>
        </div>
      )}

      {/* Header Section */}
      <div className='row mb-4'>
        <div className='col-md-12'>
          <div style={{ 
            backgroundColor: lightTheme.cardBackground, 
            border: `1px solid ${lightTheme.borderColor}`,
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <div style={{ 
              backgroundColor: lightTheme.headerBackground,
              padding: '10px 15px',
              borderRadius: '6px',
              marginBottom: '15px',
              borderLeft: `4px solid ${lightTheme.primaryButton}`
            }}>
              <div className='d-flex justify-content-between align-items-center'>
                <h5 className='m-0' style={{ color: '#495057' }}>Quotation History</h5>
                <button
                  className='btn'
                  onClick={() => navigate('/quotation')}
                  style={{
                    backgroundColor: lightTheme.successButton,
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px'
                  }}
                >
                  ➕ New Quotation
                </button>
              </div>
            </div>
            
            {/* Search Bar */}
            <div className='row mb-3'>
              <div className='col-md-8'>
                <div className="input-group">
                  <span className="input-group-text" style={{ backgroundColor: '#f8f9fa', borderColor: lightTheme.borderColor }}>
                    🔍
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by customer name, quotation number, or phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      borderColor: lightTheme.borderColor,
                      backgroundColor: '#ffffff'
                    }}
                  />
                  {searchTerm && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setSearchTerm('')}
                      style={{
                        borderColor: lightTheme.borderColor,
                        color: lightTheme.textMuted
                      }}
                      title="Clear search"
                    >
                      ✖️
                    </button>
                  )}
                </div>
              </div>
              <div className='col-md-4 text-end'>
                <small style={{ color: lightTheme.textMuted }}>
                  {filteredQuotations.length} quotation{filteredQuotations.length !== 1 ? 's' : ''} found
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quotations Table */}
      <div className='row'>
        <div className='col-md-12'>
          <div style={{ 
            backgroundColor: lightTheme.cardBackground, 
            border: `1px solid ${lightTheme.borderColor}`,
            borderRadius: '8px',
            padding: '20px'
          }}>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading quotations...</p>
              </div>
            ) : (
              <div className='table-responsive'>
                <table className='table table-hover' style={{ fontSize: '12px', backgroundColor: '#ffffff' }}>
                  <thead style={{ backgroundColor: lightTheme.tableHeader }}>
                    <tr>
                      <th style={{ fontSize: '11px', color: '#495057' }}>Quotation No</th>
                      <th style={{ fontSize: '11px', color: '#495057' }}>Date</th>
                      <th style={{ fontSize: '11px', color: '#495057' }}>Customer</th>
                      <th style={{ fontSize: '11px', color: '#495057' }}>Phone</th>
                      <th style={{ fontSize: '11px', color: '#495057' }}>Valid Until</th>
                      <th style={{ fontSize: '11px', color: '#495057' }}>Grand Total</th>
                      <th style={{ fontSize: '11px', color: '#495057' }}>Status</th>
                      <th style={{ fontSize: '11px', color: '#495057' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentQuotations.length > 0 ? (
                      currentQuotations.map((quotation) => (
                        <tr key={quotation.id} style={{ fontSize: '12px' }}>
                          <td style={{ color: '#495057' }}>
                            {quotation.quotation_number || `Q${quotation.id}`}
                          </td>
                          <td style={{ color: '#495057' }}>
                            {new Date(quotation.setup_date).toLocaleDateString('en-GB')}
                          </td>
                          <td style={{ color: '#495057' }}>
                            {quotation.customer_name || 'N/A'}
                          </td>
                          <td style={{ color: '#495057' }}>
                            {quotation.customer_phone || 'N/A'}
                          </td>
                          <td style={{ color: '#495057' }}>
                            {quotation.valid_until ? new Date(quotation.valid_until).toLocaleDateString('en-GB') : 'N/A'}
                          </td>
                          <td style={{ color: '#495057' }}>
                            ฿ {(Number(quotation.grand_total) || 0).toFixed(2)}
                          </td>
                          <td>
                            <span 
                              className={`badge ${quotation.status === 'pending' ? 'bg-warning' : 
                                quotation.status === 'approved' ? 'bg-success' : 'bg-secondary'}`}
                              style={{ fontSize: '10px' }}
                            >
                              {quotation.status || 'pending'}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <button
                                className="btn btn-sm"
                                onClick={() => handleEdit(quotation)}
                                style={{
                                  backgroundColor: lightTheme.primaryButton,
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '4px 8px'
                                }}
                                title="Edit Quotation"
                              >
                                ✏️
                              </button>
                              <button
                                className="btn btn-sm"
                                onClick={() => handlePrint(quotation)}
                                style={{
                                  backgroundColor: lightTheme.infoButton,
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '4px 8px'
                                }}
                                title="Print Quotation"
                              >
                                🖨️
                              </button>
                              <button
                                className="btn btn-sm"
                                onClick={() => confirmDelete(quotation)}
                                style={{
                                  backgroundColor: lightTheme.dangerButton,
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '4px 8px'
                                }}
                                title="Delete Quotation"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center py-4" style={{ color: lightTheme.textMuted }}>
                          {searchTerm ? 'No quotations found matching your search.' : 'No quotations found.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination Controls */}
            {filteredQuotations.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div style={{ color: lightTheme.textMuted, fontSize: '14px' }}>
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredQuotations.length)} of {filteredQuotations.length} entries
                </div>
                <div className="pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="btn btn-sm btn-outline-primary me-1"
                    style={{
                      borderColor: lightTheme.borderColor,
                      color: currentPage === 1 ? lightTheme.textMuted : lightTheme.primaryButton
                    }}
                  >
                    &laquo; Previous
                  </button>
                  {[...Array(totalPages).keys()].map((page) => (
                    <button
                      key={page + 1}
                      onClick={() => handlePageChange(page + 1)}
                      className={`btn btn-sm me-1 ${currentPage === page + 1 ? 'btn-primary' : 'btn-outline-primary'}`}
                      style={{
                        borderColor: lightTheme.borderColor,
                        backgroundColor: currentPage === page + 1 ? lightTheme.primaryButton : 'transparent',
                        color: currentPage === page + 1 ? 'white' : lightTheme.primaryButton
                      }}
                    >
                      {page + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="btn btn-sm btn-outline-primary"
                    style={{
                      borderColor: lightTheme.borderColor,
                      color: currentPage === totalPages ? lightTheme.textMuted : lightTheme.primaryButton
                    }}
                  >
                    Next &raquo;
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.5)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            minWidth: 400,
            maxWidth: '90vw',
            boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
            padding: 32,
            position: 'relative'
          }}>
            <h4 style={{ marginBottom: 16, color: '#495057' }}>Confirm Delete</h4>
            <p style={{ marginBottom: 24, color: '#6c757d' }}>
              Are you sure you want to delete quotation "{quotationToDelete?.quotation_number || `Q${quotationToDelete?.id}`}"? 
              This action cannot be undone.
            </p>
            <div className="d-flex gap-2 justify-content-end">
              <button
                className="btn"
                onClick={() => {
                  setShowDeleteModal(false);
                  setQuotationToDelete(null);
                }}
                style={{
                  backgroundColor: lightTheme.borderColor,
                  color: '#495057',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px'
                }}
              >
                Cancel
              </button>
              <button
                className="btn"
                onClick={() => handleDelete(quotationToDelete.id)}
                style={{
                  backgroundColor: lightTheme.dangerButton,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Layout>
      {renderContent()}
    </Layout>
  );
}
